import * as React from 'react';
import { FormControl, FormControlLabel, FormHelperText } from '@mui/material';
import {
  FormControlLabelProps,
  FormControlLabelProps as MuiFormControlLabelProps,
} from '@mui/material/FormControlLabel';
import { SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import {
  switchField,
  switchFieldFormControlLabel,
  switchFieldHelperText,
} from 'ui/SwitchField/switchFieldCssDefs';
import Switch from 'ui/Switch/Switch';

export interface SwitchFieldProps {
  checked?: boolean;
  disabled?: boolean;
  error?: boolean;
  FormControlLabelProps?: MuiFormControlLabelProps;
  fullWidth?: boolean;
  helperText?: string;
  id?: string;
  label?: string;
  labelClasses?: FormControlLabelProps['classes'];
  labelPlacement?: 'end' | 'start';
  name?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  size?: 'large' | 'medium' | 'small';
  SwitchProps?: MuiSwitchProps;
  value?: string;
}

/**
 * Use `Switch` for rendering a switch, see stories for more complete examples. This is a wrapper for Switch
 * that includes FormControlLabel, Switch, and FormHelperText
 *
 * ```js
 * import { SwitchField } from 'ui';
 * ```
 * @deprecated Fine to use for Tock internal tools only. Please use `@tocktix/pantry` UI components for any new JCE admin tools.
 */
const SwitchField: React.FunctionComponent<React.PropsWithChildren<SwitchFieldProps>> = ({
  label,
  labelClasses,
  labelPlacement,
  disabled,
  SwitchProps,
  FormControlLabelProps,
  fullWidth,
  helperText,
  error,
  checked,
  value,
  onChange,
  size,
  name,
  id,
}) => {
  return (
    <FormControl
      error={error}
      disabled={disabled ?? FormControlLabelProps?.disabled}
      fullWidth={fullWidth}
    >
      <FormControlLabel
        css={() => switchFieldFormControlLabel(fullWidth, labelPlacement)}
        classes={{
          ...labelClasses,
        }}
        {...FormControlLabelProps}
        disabled={disabled ?? FormControlLabelProps?.disabled}
        label={label ?? FormControlLabelProps?.label}
        labelPlacement={labelPlacement ?? FormControlLabelProps?.labelPlacement}
        control={
          <Switch
            css={switchField}
            size={size}
            color="primary"
            {...SwitchProps}
            id={id ?? SwitchProps?.id}
            inputProps={{
              ...SwitchProps?.inputProps,
              name: name ?? SwitchProps?.name,
            }}
            checked={checked ?? SwitchProps?.checked}
            onChange={onChange ?? SwitchProps?.onChange}
            value={value ?? SwitchProps?.value}
          />
        }
      />
      {helperText && <FormHelperText css={switchFieldHelperText}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default SwitchField;
